import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const tocRef = useRef(null);
  const activeTocItemRef = useRef(null);
  const isScrollingTocRef = useRef(false);

  useEffect(() => {
    document.title = 'Privacy Policy - Dishant Madariya | Full Stack Developer';
    window.scrollTo(0, 0);

    // Handle sticky TOC
    const handleScroll = () => {
      setIsSticky(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for detecting active sections
  useEffect(() => {
    const sections = document.querySelectorAll('[data-section]');
    if (sections.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Trigger when section is in upper portion of viewport
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    const observerCallback = (entries) => {
      // Find the entry with the highest intersection ratio that's intersecting
      let maxRatio = 0;
      let activeEntry = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          activeEntry = entry;
        }
      });

      // If no intersecting entry, find the one closest to the top
      if (!activeEntry) {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          if (rect.top >= 0 && rect.top < window.innerHeight * 0.3) {
            activeEntry = entry;
          }
        });
      }

      if (activeEntry && activeEntry.isIntersecting) {
        const sectionId = activeEntry.target.getAttribute('id');
        if (sectionId) {
          setActiveSection((prev) => {
            // Only update if different to prevent unnecessary re-renders
            return prev !== sectionId ? sectionId : prev;
          });
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []); // Empty dependency array - only run once on mount

  // Auto-scroll TOC sidebar to keep active item visible
  useEffect(() => {
    if (!activeSection || isScrollingTocRef.current) return;

    const tocContainer = tocRef.current;
    const activeTocItem = activeTocItemRef.current;

    if (tocContainer && activeTocItem) {
      const containerRect = tocContainer.getBoundingClientRect();
      const itemRect = activeTocItem.getBoundingClientRect();

      // Check if item is outside viewport
      const isAboveViewport = itemRect.top < containerRect.top;
      const isBelowViewport = itemRect.bottom > containerRect.bottom;

      if (isAboveViewport || isBelowViewport) {
        isScrollingTocRef.current = true;

        // Calculate scroll position to center the active item
        const containerScrollTop = tocContainer.scrollTop;
        const itemOffsetTop = activeTocItem.offsetTop;
        const containerHeight = tocContainer.clientHeight;
        const itemHeight = activeTocItem.offsetHeight;

        // Center the item in the container
        const targetScrollTop = itemOffsetTop - (containerHeight / 2) + (itemHeight / 2);

        tocContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });

        // Reset flag after scroll completes
        setTimeout(() => {
          isScrollingTocRef.current = false;
        }, 500);
      }
    }
  }, [activeSection]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Temporarily disable auto-scroll to prevent conflicts
      isScrollingTocRef.current = true;
      
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Re-enable auto-scroll after a delay
      setTimeout(() => {
        isScrollingTocRef.current = false;
      }, 1000);
    }
  };

  const tableOfContents = [
    { id: 'introduction', label: 'Introduction', number: 1 },
    { id: 'data-collection', label: 'Information We Collect', number: 2 },
    { id: 'data-usage', label: 'How We Use Your Information', number: 3 },
    { id: 'data-sharing', label: 'Information Sharing & Disclosure', number: 4 },
    { id: 'data-security', label: 'Data Security', number: 5 },
    { id: 'data-retention', label: 'Data Retention', number: 6 },
    { id: 'your-rights', label: 'Your Rights & Choices', number: 7 },
    { id: 'cookies', label: 'Cookies & Tracking Technologies', number: 8 },
    { id: 'third-party', label: 'Third-Party Services', number: 9 },
    { id: 'children', label: 'Children\'s Privacy', number: 10 },
    { id: 'international', label: 'International Data Transfers', number: 11 },
    { id: 'updates', label: 'Policy Updates', number: 12 },
    { id: 'contact', label: 'Contact Information', number: 13 },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - Dishant Madariya | Full Stack Developer</title>
        <meta name="description" content="Privacy Policy for Dishant Madariya's professional services. Learn how we collect, use, and protect your personal information." />
      </Helmet>
      
      <SectionScrollIndicator />
      
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Privacy Policy
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-4">
              Last Updated: December 8, 2025
            </p>
            <p className="text-sm md:text-base text-text-secondary max-w-3xl mx-auto">
              This Privacy Policy describes how Dishant Madariya collects, uses, and protects your personal information when you use our services or visit our website. Your privacy is important to us, and we are committed to protecting your personal data.
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Left Column - Table of Contents (Sticky on Desktop) */}
            <div className="lg:col-span-1">
              <div className={`lg:sticky ${isSticky ? 'lg:top-24' : 'lg:top-20'} transition-all duration-300`}>
                <div className="bg-surface rounded-lg border border-border p-6 shadow-md">
                  <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                    <Icon name="List" size={20} className="mr-2 text-primary" />
                    Table of Contents
                  </h2>
                  <nav 
                    ref={tocRef}
                    className="toc-scrollbar space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto overscroll-contain pr-2"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        ref={activeSection === item.id ? activeTocItemRef : null}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                          activeSection === item.id
                            ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                            : 'text-text-secondary hover:bg-muted hover:text-text-primary'
                        }`}
                      >
                        <span className="font-medium">{item.number}.</span> {item.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Right Column - Main Content */}
            <div className="lg:col-span-3 space-y-8 text-left break-words whitespace-normal min-w-0">
              {/* Introduction */}
              <section id="introduction" data-section="introduction" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">1</span>
                    Introduction
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">1.1 About This Policy</h3>
                      <p className="text-text-secondary leading-relaxed">
                        This Privacy Policy explains how Dishant Madariya ("we," "our," or "us") collects, uses, discloses, and safeguards your personal information when you interact with our website, services, or engage with us for professional services. We respect your privacy and are committed to protecting your personal data in accordance with applicable privacy laws, including the General Data Protection Regulation (GDPR) and India's Digital Personal Data Protection Act.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">1.2 Data Controller</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">
                        For the purposes of this Privacy Policy, the data controller is:
                      </p>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <p className="font-semibold text-text-primary mb-2">Dishant Madariya</p>
                        <p className="text-sm text-text-secondary">Full Stack Developer</p>
                        <p className="text-sm text-text-secondary mt-2">Email: contact.dishantpatel@gmail.com</p>
                        <p className="text-sm text-text-secondary">Location: Surat, Gujarat, India</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">1.3 Your Consent</h3>
                      <p className="text-text-secondary leading-relaxed">
                        By using our website or services, you consent to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Information We Collect */}
              <section id="data-collection" data-section="data-collection" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">2</span>
                    Information We Collect
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.1 Information You Provide Directly</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We collect information that you voluntarily provide to us, including:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
                        <li><strong>Project Information:</strong> Project requirements, specifications, business information</li>
                        <li><strong>Communication Data:</strong> Messages, emails, and other communications you send to us</li>
                        <li><strong>Payment Information:</strong> Billing details, payment method information (processed securely through third-party payment processors)</li>
                        <li><strong>Account Information:</strong> Username, password (hashed and encrypted), and other account credentials if you create an account</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.2 Automatically Collected Information</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">When you visit our website, we automatically collect certain information:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, navigation paths</li>
                        <li><strong>Device Information:</strong> IP address, browser type and version, device type, operating system</li>
                        <li><strong>Location Data:</strong> General geographic location based on IP address (city/country level)</li>
                        <li><strong>Log Data:</strong> Server logs, access times, error logs, and system information</li>
                        <li><strong>Cookies and Tracking Technologies:</strong> See Section 8 for detailed information</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.3 Information from Third Parties</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We may receive information about you from third-party sources:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Social media platforms (if you interact with us through social media)</li>
                        <li>Payment processors (transaction information)</li>
                        <li>Analytics providers (website usage statistics)</li>
                        <li>Business partners or referrals</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.4 Sensitive Personal Information</h3>
                      <p className="text-text-secondary leading-relaxed">
                        We do not intentionally collect sensitive personal information (such as race, religion, health information, or biometric data) unless required for specific services and with your explicit consent. If such information is necessary, we will obtain your explicit consent and handle it with additional security measures.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* How We Use Your Information */}
              <section id="data-usage" data-section="data-usage" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">3</span>
                    How We Use Your Information
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.1 Service Provision</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>To provide, maintain, and improve our professional services</li>
                        <li>To process and manage project engagements and contracts</li>
                        <li>To communicate with you about your projects, inquiries, and requests</li>
                        <li>To deliver project deliverables and provide technical support</li>
                        <li>To process payments and manage billing</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.2 Communication</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>To respond to your inquiries, comments, and requests</li>
                        <li>To send you project updates, notifications, and important information</li>
                        <li>To provide customer support and technical assistance</li>
                        <li>To send marketing communications (only with your consent, and you can opt-out anytime)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.3 Website Improvement</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>To analyze website usage and user behavior</li>
                        <li>To improve website functionality, user experience, and content</li>
                        <li>To identify and fix technical issues</li>
                        <li>To conduct research and analytics</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.4 Legal and Compliance</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>To comply with applicable laws, regulations, and legal obligations</li>
                        <li>To enforce our Terms & Conditions and other agreements</li>
                        <li>To protect our rights, property, and safety, as well as that of our clients</li>
                        <li>To prevent fraud, abuse, and other illegal activities</li>
                        <li>To respond to legal requests and court orders</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.5 Business Operations</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>To manage our business operations and administrative functions</li>
                        <li>To maintain records and documentation</li>
                        <li>To conduct business analytics and reporting</li>
                        <li>To improve our services and develop new offerings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Information Sharing & Disclosure */}
              <section id="data-sharing" data-section="data-sharing" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">4</span>
                    Information Sharing & Disclosure
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.1 We Do Not Sell Your Data</h3>
                      <p className="text-text-secondary leading-relaxed">
                        We do not sell, rent, or trade your personal information to third parties for their marketing purposes. Your privacy is important to us, and we maintain strict confidentiality of your data.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.2 Service Providers</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We may share your information with trusted third-party service providers who assist us in operating our business:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Hosting Providers:</strong> To host our website and services</li>
                        <li><strong>Payment Processors:</strong> To process payments securely</li>
                        <li><strong>Analytics Services:</strong> To analyze website usage and improve our services</li>
                        <li><strong>Email Services:</strong> To send communications and notifications</li>
                        <li><strong>Cloud Storage:</strong> To store and backup data securely</li>
                      </ul>
                      <p className="text-text-secondary leading-relaxed mt-3">
                        These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.3 Legal Requirements</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We may disclose your information if required by law or in response to:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Court orders, subpoenas, or legal processes</li>
                        <li>Government requests or regulatory requirements</li>
                        <li>To protect our rights, property, or safety</li>
                        <li>To prevent fraud or illegal activities</li>
                        <li>To enforce our Terms & Conditions</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.4 Business Transfers</h3>
                      <p className="text-text-secondary leading-relaxed">
                        In the event of a merger, acquisition, sale of assets, or business transfer, your information may be transferred to the acquiring entity. We will notify you of any such change in ownership and provide you with choices regarding your information.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.5 With Your Consent</h3>
                      <p className="text-text-secondary leading-relaxed">
                        We may share your information with third parties when you explicitly consent to such sharing, such as when you request a referral or collaboration with another service provider.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Security */}
              <section id="data-security" data-section="data-security" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">5</span>
                    Data Security
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.1 Security Measures</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We implement appropriate technical and organizational measures to protect your personal information:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Encryption:</strong> Data transmitted over the internet is encrypted using SSL/TLS protocols</li>
                        <li><strong>Secure Storage:</strong> Personal data is stored on secure servers with access controls</li>
                        <li><strong>Access Controls:</strong> Limited access to personal information on a need-to-know basis</li>
                        <li><strong>Regular Updates:</strong> Security systems and software are kept up-to-date</li>
                        <li><strong>Backup Systems:</strong> Regular backups to prevent data loss</li>
                        <li><strong>Security Monitoring:</strong> Continuous monitoring for security threats and vulnerabilities</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.2 No Absolute Security</h3>
                      <p className="text-text-secondary leading-relaxed">
                        While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we are committed to using industry-standard security practices to protect your data.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.3 Data Breach Notification</h3>
                      <p className="text-text-secondary leading-relaxed">
                        In the event of a data breach that poses a risk to your rights and freedoms, we will notify you and relevant authorities as required by applicable law, typically within 72 hours of becoming aware of the breach.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.4 Your Role in Security</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">You also play a role in protecting your information:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Use strong, unique passwords for any accounts you create</li>
                        <li>Do not share your account credentials with others</li>
                        <li>Keep your devices and software updated</li>
                        <li>Be cautious when sharing personal information online</li>
                        <li>Report any suspicious activity immediately</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Retention */}
              <section id="data-retention" data-section="data-retention" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">6</span>
                    Data Retention
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.1 Retention Period</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We retain your personal information only for as long as necessary to:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Fulfill the purposes for which it was collected</li>
                        <li>Comply with legal, accounting, or reporting requirements</li>
                        <li>Resolve disputes and enforce our agreements</li>
                        <li>Maintain business records as required by law</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.2 Specific Retention Periods</h3>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Project-Related Data</h4>
                          <p className="text-sm text-text-secondary">Retained for the duration of the project plus 7 years for legal and accounting purposes</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Marketing Communications</h4>
                          <p className="text-sm text-text-secondary">Retained until you unsubscribe or request deletion</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Website Analytics</h4>
                          <p className="text-sm text-text-secondary">Retained for up to 26 months (as per Google Analytics default)</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Legal Records</h4>
                          <p className="text-sm text-text-secondary">Retained as required by applicable law (typically 7-10 years)</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.3 Deletion</h3>
                      <p className="text-text-secondary leading-relaxed">
                        When personal information is no longer needed, we will securely delete or anonymize it. You may also request deletion of your personal information at any time, subject to legal retention requirements.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Your Rights & Choices */}
              <section id="your-rights" data-section="your-rights" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">7</span>
                    Your Rights & Choices
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.1 Access Rights</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to request access to the personal information we hold about you, including details about how we use it and who we share it with.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.2 Correction Rights</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to request correction of inaccurate or incomplete personal information. We will update your information promptly upon verification.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.3 Deletion Rights</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to request deletion of your personal information, subject to legal obligations and legitimate business interests that require retention.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.4 Data Portability</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to receive your personal information in a structured, commonly used, and machine-readable format, and to transmit it to another service provider where technically feasible.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.5 Objection Rights</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to object to processing of your personal information for certain purposes, such as direct marketing. We will respect your objection unless we have compelling legitimate grounds to continue processing.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.6 Restriction Rights</h3>
                      <p className="text-text-secondary leading-relaxed">
                        You have the right to request restriction of processing of your personal information in certain circumstances, such as when you contest the accuracy of the data.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.7 Withdraw Consent</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Where processing is based on your consent, you have the right to withdraw consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.8 Marketing Opt-Out</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">You can opt-out of marketing communications at any time by:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Clicking the unsubscribe link in marketing emails</li>
                        <li>Contacting us directly at contact.dishantpatel@gmail.com</li>
                        <li>Updating your preferences in your account settings (if applicable)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.9 Exercising Your Rights</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">To exercise any of these rights, please contact us at:</p>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <p className="text-sm text-text-secondary">Email: contact.dishantpatel@gmail.com</p>
                        <p className="text-sm text-text-secondary mt-2">We will respond to your request within 30 days (or as required by applicable law).</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Cookies & Tracking Technologies */}
              <section id="cookies" data-section="cookies" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">8</span>
                    Cookies & Tracking Technologies
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.1 What Are Cookies</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and improve your browsing experience.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.2 Types of Cookies We Use</h3>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Essential Cookies</h4>
                          <p className="text-sm text-text-secondary">Required for the website to function properly. These cannot be disabled.</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Analytics Cookies</h4>
                          <p className="text-sm text-text-secondary">Help us understand how visitors interact with our website (e.g., Google Analytics).</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Functional Cookies</h4>
                          <p className="text-sm text-text-secondary">Remember your preferences and settings to enhance your experience.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.3 Managing Cookies</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">You can control and manage cookies through:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Your browser settings (most browsers allow you to block or delete cookies)</li>
                        <li>Cookie consent banners on our website</li>
                        <li>Third-party opt-out tools (e.g., Google Analytics Opt-out)</li>
                      </ul>
                      <p className="text-text-secondary leading-relaxed mt-3">
                        Note: Disabling certain cookies may affect website functionality and your user experience.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Third-Party Services */}
              <section id="third-party" data-section="third-party" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">9</span>
                    Third-Party Services
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.1 Third-Party Links</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Our website may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.2 Third-Party Services We Use</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">We use the following third-party services that may collect information:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Google Analytics:</strong> Website analytics and usage statistics</li>
                        <li><strong>Payment Processors:</strong> Secure payment processing (e.g., PayPal, Stripe)</li>
                        <li><strong>Hosting Services:</strong> Website and data hosting</li>
                        <li><strong>Email Services:</strong> Email delivery and management</li>
                      </ul>
                      <p className="text-text-secondary leading-relaxed mt-3">
                        Each service has its own privacy policy, and we recommend reviewing them to understand how they handle your data.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Children's Privacy */}
              <section id="children" data-section="children" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">10</span>
                    Children's Privacy
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.1 Age Restrictions</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will delete such information.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* International Data Transfers */}
              <section id="international" data-section="international" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">11</span>
                    International Data Transfers
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.1 Data Location</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Your personal information may be stored and processed in India or other countries where our service providers operate. By using our services, you consent to the transfer of your information to these locations.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.2 Data Protection Safeguards</h3>
                      <p className="text-text-secondary leading-relaxed">
                        When transferring data internationally, we ensure appropriate safeguards are in place, such as standard contractual clauses, to protect your personal information in accordance with applicable privacy laws.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Policy Updates */}
              <section id="updates" data-section="updates" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">12</span>
                    Policy Updates
                  </h2>
                  
                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.1 Changes to This Policy</h3>
                      <p className="text-text-secondary leading-relaxed">
                        We may update this Privacy Policy from time to time to reflect changes in our practices, services, or legal requirements. We will notify you of any material changes by:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 mt-3">
                        <li>Posting the updated policy on our website with a new "Last Updated" date</li>
                        <li>Sending an email notification to registered users (if applicable)</li>
                        <li>Displaying a prominent notice on our website</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.2 Continued Use</h3>
                      <p className="text-text-secondary leading-relaxed">
                        Your continued use of our services after any changes to this Privacy Policy constitutes acceptance of the updated policy. We encourage you to review this policy periodically.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section id="contact" data-section="contact" className="scroll-mt-24">
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6 md:p-8 shadow-md border border-primary/20">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">13</span>
                    Contact Information
                  </h2>
                  
                  <div className="space-y-4 ml-[calc(2.5rem+1rem)]">
                    <p className="text-text-secondary leading-relaxed">
                      If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                    </p>
                    
                    <div className="bg-surface rounded-lg p-6 border border-border">
                      <h3 className="text-xl font-semibold text-text-primary mb-4">Dishant Madariya</h3>
                      <p className="text-text-secondary mb-4 font-medium">Full Stack Developer</p>
                      
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <Icon name="Mail" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Email</p>
                            <a href="mailto:contact.dishantpatel@gmail.com" className="text-primary hover:underline text-sm">
                              contact.dishantpatel@gmail.com
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Icon name="Globe" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Website</p>
                            <a href="https://port-folio-three-teal.vercel.app/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                              https://port-folio-three-teal.vercel.app/
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Icon name="MapPin" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Location</p>
                            <p className="text-text-secondary text-sm">Surat, Gujarat, India</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 border border-border mt-4">
                      <p className="text-sm text-text-secondary">
                        <strong>Response Time:</strong> We aim to respond to all privacy-related inquiries within 30 days, as required by applicable privacy laws.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Acceptance Section */}
              <section className="scroll-mt-24">
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 md:p-8 shadow-md border-2 border-primary/30">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">Acceptance of Privacy Policy</h2>
                  <p className="text-text-secondary leading-relaxed mb-4">
                    By using our website or services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your personal information as described herein.
                  </p>
                  <p className="text-text-secondary leading-relaxed font-medium">
                    If you do not agree with this Privacy Policy, please do not use our services.
                  </p>
                  <div className="mt-6 pt-6 border-t border-border">
                    <p className="text-sm text-text-secondary">
                      <strong>Last Updated:</strong> December 8, 2025<br />
                      <strong>Version:</strong> 1.0
                    </p>
                    <p className="text-sm text-text-secondary mt-2 italic">
                      This Privacy Policy is effective as of the date stated above and applies to all information collected from that date forward.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;

